/**
 * Theme Presets - Premium, Metallic, and Pastel
 * Used across builder, onboarding, and branding
 */

export interface ThemePreset {
  key: string;
  name: string;
  primary: string;
  secondary: string;
  description: string;
  category: "premium" | "metallic" | "pastel";
}

// Premium color palettes
export const premiumPresets: ThemePreset[] = [
  { key: "dream", name: "Dream", primary: "#87CEEB", secondary: "#B0E0E6", description: "Soft cyan, heavenly blue", category: "premium" },
  { key: "professional", name: "Professional", primary: "#1a1a2e", secondary: "#16213e", description: "Deep navy, sophisticated", category: "premium" },
  { key: "bold", name: "Bold", primary: "#FF6B6B", secondary: "#FF8E53", description: "Vibrant coral, energetic", category: "premium" },
  { key: "playful", name: "Playful", primary: "#A78BFA", secondary: "#C084FC", description: "Rich purple, creative", category: "premium" },
  { key: "sophisticated", name: "Sophisticated", primary: "#0EA5E9", secondary: "#3B82F6", description: "Ocean blue, refined", category: "premium" },
  { key: "friendly", name: "Friendly", primary: "#10B981", secondary: "#34D399", description: "Fresh green, approachable", category: "premium" },
  { key: "sunset", name: "Sunset", primary: "#F59E0B", secondary: "#EF4444", description: "Warm orange-red, inspiring", category: "premium" },
  { key: "lavender", name: "Lavender", primary: "#C084FC", secondary: "#E879F9", description: "Soft purple-pink, elegant", category: "premium" },
  { key: "ocean", name: "Ocean", primary: "#06B6D4", secondary: "#3B82F6", description: "Turquoise-blue, calming", category: "premium" },
  { key: "forest", name: "Forest", primary: "#059669", secondary: "#10B981", description: "Deep green, natural", category: "premium" },
  { key: "rose", name: "Rose", primary: "#EC4899", secondary: "#F472B6", description: "Pink-rose, romantic", category: "premium" },
  { key: "midnight", name: "Midnight", primary: "#6366F1", secondary: "#8B5CF6", description: "Indigo-purple, mysterious", category: "premium" },
];

// Metallic color presets
export const metallicPresets: ThemePreset[] = [
  { key: "gold", name: "Gold", primary: "#D4AF37", secondary: "#FFD700", description: "Luxurious gold", category: "metallic" },
  { key: "silver", name: "Silver", primary: "#C0C0C0", secondary: "#E8E8E8", description: "Elegant silver", category: "metallic" },
  { key: "bronze", name: "Bronze", primary: "#CD7F32", secondary: "#E6A857", description: "Warm bronze", category: "metallic" },
  { key: "copper", name: "Copper", primary: "#B87333", secondary: "#D2691E", description: "Rich copper", category: "metallic" },
  { key: "platinum", name: "Platinum", primary: "#E5E4E2", secondary: "#F5F5F5", description: "Premium platinum", category: "metallic" },
  { key: "rose-gold", name: "Rose Gold", primary: "#E8B4B8", secondary: "#F4C2C2", description: "Sophisticated rose gold", category: "metallic" },
];

// Pastel color presets
export const pastelPresets: ThemePreset[] = [
  { key: "pastel-lavender", name: "Lavender", primary: "#E6E6FA", secondary: "#DDA0DD", description: "Soft lavender", category: "pastel" },
  { key: "pastel-mint", name: "Mint", primary: "#F0FFF0", secondary: "#98FB98", description: "Fresh mint", category: "pastel" },
  { key: "pastel-peach", name: "Peach", primary: "#FFE5B4", secondary: "#FFCCCB", description: "Warm peach", category: "pastel" },
  { key: "pastel-sky", name: "Sky", primary: "#E0F6FF", secondary: "#B0E0E6", description: "Calm sky blue", category: "pastel" },
  { key: "pastel-blush", name: "Blush", primary: "#FFE4E1", secondary: "#FFB6C1", description: "Gentle blush", category: "pastel" },
  { key: "pastel-lemon", name: "Lemon", primary: "#FFFACD", secondary: "#FFFFE0", description: "Bright lemon", category: "pastel" },
  { key: "pastel-lilac", name: "Lilac", primary: "#E6D9EC", secondary: "#D8BFD8", description: "Delicate lilac", category: "pastel" },
  { key: "pastel-sage", name: "Sage", primary: "#E8F5E9", secondary: "#C5E1A5", description: "Natural sage", category: "pastel" },
];

// All presets combined
export const allThemePresets: ThemePreset[] = [
  ...premiumPresets,
  ...metallicPresets,
  ...pastelPresets,
];

// Get theme by key
export function getThemeByKey(key: string): ThemePreset | undefined {
  return allThemePresets.find(p => p.key === key);
}

// Get random theme
export function getRandomTheme(): ThemePreset {
  const themes = allThemePresets;
  return themes[Math.floor(Math.random() * themes.length)];
}

